import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import ManagerLayout from "./ManagerLayout";
import { toast, Toaster } from "sonner";
import { Lock, Trash2, CalendarPlus, ExternalLink } from "lucide-react";
import { SLOT_LEGEND_STAFF } from "../../lib/slotColors";
import PhoneInput from "../../components/PhoneInput";
import { gameTypeLabel } from "../../lib/facilities";
import MultiDateSlotGrid from "../../components/MultiDateSlotGrid";
import CustomerNameAutocomplete from "../../components/CustomerNameAutocomplete";
import { localYMD } from "../../lib/dates";

function fmtDate(d) { return localYMD(d); }

export default function ManagerSlots() {
    const [centres, setCentres] = useState([]);
    const [centreId, setCentreId] = useState("");
    const [centre, setCentre] = useState(null);
    const [facilityId, setFacilityId] = useState("");
    const [date, setDate] = useState(fmtDate(new Date()));
    const [selected, setSelected] = useState([]);
    const [multi] = useState(true); // managers always operate in multi-date select mode
    const [selectedCells, setSelectedCells] = useState(new Set());
    const [reason, setReason] = useState("blocked");
    const [submitting, setSubmitting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [blocks, setBlocks] = useState([]);

    const [bookOpen, setBookOpen] = useState(false);
    const [bookForm, setBookForm] = useState({ customer_name: "", customer_phone: "", payment_method: "cash", paid_amount: "", discount: "", notes: "", notify_customer: false });
    const [extOpen, setExtOpen] = useState(false);
    const [extForm, setExtForm] = useState({ external_source: "Hudle", notes: "", amount_to_collect: "" });
    const [unblockTarget, setUnblockTarget] = useState(null);
    const [slotPriceSum, setSlotPriceSum] = useState(0);   // Iter 40 — sum of per-slot prices

    useEffect(() => {
        api.get("/manager/centres").then(({ data }) => {
            setCentres(data);
            if (data.length) setCentreId(data[0].id);
        });
    }, []);

    useEffect(() => {
        if (!centreId) return;
        api.get(`/centres/${centreId}`).then(({ data }) => {
            setCentre(data);
            const facs = (data.facilities || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            setFacilityId(facs[0]?.id || "");
        });
    }, [centreId]);

    const facility = useMemo(() => (centre?.facilities || []).find(f => f.id === facilityId), [centre, facilityId]);
    const sortedFacilities = useMemo(() => (
        (centre?.facilities || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    ), [centre]);

    useEffect(() => {
        if (!centreId) return;
        api.get("/slot-blocks", { params: { centre_id: centreId, booking_date: date } })
            .then(({ data }) => setBlocks(data));
    }, [centreId, date, refreshKey]);

    const byDate = useMemo(() => {
        const out = {};
        for (const k of selectedCells) {
            const [d, t] = k.split("|");
            (out[d] = out[d] || []).push(t);
        }
        for (const d of Object.keys(out)) out[d].sort();
        return out;
    }, [selectedCells]);
    const dateKeys = useMemo(() => Object.keys(byDate).sort(), [byDate]);
    const singleDate = dateKeys.length === 1 ? dateKeys[0] : null;
    const totalCells = selectedCells.size;

    const onSelectionChange = (newDate, newTimes) => {
        setDate(newDate);
        setSelected(newTimes);
    };

    const blockNow = async () => {
        if (totalCells === 0) return;
        setSubmitting(true);
        try {
            const firstSlots = JSON.stringify(byDate[dateKeys[0]]);
            const allSame = dateKeys.every(d => JSON.stringify(byDate[d]) === firstSlots);
            if (allSame && dateKeys.length > 1) {
                await api.post("/slot-blocks/bulk", {
                    centre_id: centreId, facility_id: facilityId,
                    booking_dates: dateKeys, slots: byDate[dateKeys[0]], reason,
                });
            } else {
                for (const d of dateKeys) {
                    await api.post("/slot-blocks", {
                        centre_id: centreId, facility_id: facilityId,
                        booking_date: d, slots: byDate[d], reason,
                    });
                }
            }
            toast.success(`${totalCells} slot(s) blocked across ${dateKeys.length} day(s)`);
            setSelectedCells(new Set());
            setRefreshKey(k => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const removeBlock = async (id) => {
        await api.delete(`/slot-blocks/${id}`);
        toast.success("Block removed");
        setRefreshKey(k => k + 1);
    };

    const openBookModal = () => {
        if (!singleDate) { toast.error("Manual booking is one date at a time. Please pick slots from a single date."); return; }
        setBookForm({ customer_name: "", customer_phone: "", payment_method: "cash", paid_amount: "", discount: "", notes: "", notify_customer: false });
        setBookOpen(true);
    };

    const openExtModal = () => {
        if (!singleDate) { toast.error("External booking is one date at a time. Please pick slots from a single date."); return; }
        setExtForm({ external_source: "Hudle", notes: "", amount_to_collect: "" });
        setExtOpen(true);
    };

    const submitExternalBook = async (e) => {
        e.preventDefault();
        if (!singleDate) return;
        const src = (extForm.external_source || "").trim();
        if (!src) { toast.error("Please enter the source app name (e.g. Hudle)"); return; }
        setSubmitting(true);
        try {
            const payload = {
                centre_id: centreId, facility_id: facilityId,
                booking_date: singleDate, slots: byDate[singleDate],
                customer_name: `Booked via ${src}`,
                customer_phone: "",
                payment_method: "external",
                paid_amount: 0,
                external_source: src,
                amount_to_collect: extForm.amount_to_collect === "" ? 0 : Math.max(0, parseInt(extForm.amount_to_collect) || 0),
                notes: extForm.notes || "",
            };
            const { data } = await api.post("/manual-bookings", payload);
            toast.success(`External booking ${data.booking_code} created`);
            setExtOpen(false);
            setSelectedCells(new Set());
            setRefreshKey(k => k + 1);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const submitManualBook = async (e) => {
        e.preventDefault();
        if (!singleDate) return;
        if (!bookForm.customer_name || bookForm.customer_name.length < 2) { toast.error("Customer name is required"); return; }
        const phoneFull = bookForm.customer_phone ? `+91${bookForm.customer_phone}` : "";
        if (!/^\+91[6-9]\d{9}$/.test(phoneFull)) { toast.error("Valid 10-digit phone starting with 6-9 required"); return; }
        setSubmitting(true);
        try {
            const payload = {
                centre_id: centreId, facility_id: facilityId,
                booking_date: singleDate, slots: byDate[singleDate],
                customer_name: bookForm.customer_name, customer_phone: phoneFull,
                payment_method: bookForm.payment_method,
                paid_amount: bookForm.paid_amount === "" ? null : parseInt(bookForm.paid_amount),
                discount: bookForm.discount === "" ? 0 : Math.max(0, parseInt(bookForm.discount) || 0),
                notes: bookForm.notes || "",
                notify_customer: !!bookForm.notify_customer,
            };
            const { data } = await api.post("/manual-bookings", payload);
            toast.success(`Booked ${data.booking_code}`);
            setBookOpen(false);
            setSelectedCells(new Set());
            setRefreshKey(k => k + 1);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ManagerLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">Slot Management</h1>
                <p className="text-slate-400 text-sm mt-1">Book or block slots in your assigned centres. Booking a facility locks all linked turfs.</p>

                {centres.length === 0 && (
                    <div className="mt-6 p-6 bg-slate-900 border border-slate-800 rounded-lg text-center text-slate-500">
                        You haven't been assigned any centre yet. Please contact admin.
                    </div>
                )}

                {centres.length > 0 && (
                    <>
                        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Centre</label>
                                <select value={centreId} onChange={(e) => setCentreId(e.target.value)} data-testid="mgr-slot-centre"
                                    className="w-full mt-1 h-10 px-3 bg-slate-900 border border-slate-700 rounded text-white">
                                    {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Facility</label>
                                <select value={facilityId} onChange={(e) => setFacilityId(e.target.value)} data-testid="mgr-slot-facility"
                                    className="w-full mt-1 h-10 px-3 bg-slate-900 border border-slate-700 rounded text-white">
                                    {sortedFacilities.length === 0 && <option value="">— No facilities defined —</option>}
                                    {sortedFacilities.map(f => <option key={f.id} value={f.id}>{f.name} · ₹{f.hourly_price}/hr</option>)}
                                </select>
                                {facility && (
                                    <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">
                                        {gameTypeLabel(facility.game_type)} · locks {facility.turf_ids?.length || 0} turf{(facility.turf_ids?.length || 0) === 1 ? "" : "s"}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Block Reason</label>
                                <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mgr-slot-reason"
                                    className="w-full mt-1 h-10 px-3 bg-slate-900 border border-slate-700 rounded text-white">
                                    <option value="blocked">General block</option>
                                    <option value="maintenance">Maintenance</option>
                                    <option value="event">Event/Tournament</option>
                                    <option value="academy">Academy/Coaching</option>
                                    <option value="hold">Manual hold</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
                                    {totalCells > 0
                                        ? <>Selected: <span className="text-white">{totalCells}</span> across <span className="text-white">{dateKeys.length}</span> day{dateKeys.length === 1 ? "" : "s"}</>
                                        : "Pick slots from any column below (multi-date supported)"}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
                                {SLOT_LEGEND_STAFF.map(l => (
                                    <span key={l.label} className="inline-flex items-center gap-1">
                                        <span className={`w-3 h-3 rounded-sm ${l.swatch}`} />{l.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-3">
                            <MultiDateSlotGrid
                                centreId={centreId}
                                facilityId={facilityId}
                                selectedDate={date}
                                selectedTimes={selected}
                                onSelectionChange={onSelectionChange}
                                multiSelectMode={multi}
                                selectedCells={selectedCells}
                                onCellsChange={setSelectedCells}
                                onBlockedClick={(block, d, t) => setUnblockTarget({ block, date: d, time: t })}
                                refreshKey={refreshKey}
                                showExternalOrange={true}
                                onPriceSum={setSlotPriceSum}
                            />
                        </div>

                        <div className="mt-4 flex flex-wrap justify-end gap-3">
                            <button onClick={openBookModal} disabled={submitting || totalCells === 0 || !singleDate}
                                data-testid="mgr-manual-book-btn"
                                title={!singleDate ? "Manual booking requires slots from a single date" : ""}
                                className="inline-flex items-center gap-2 px-5 h-11 bg-sky-400 text-slate-950 font-bold uppercase tracking-widest text-xs rounded hover:bg-sky-300 disabled:opacity-40">
                                <CalendarPlus className="w-4 h-4" /> Book {singleDate ? totalCells : 0} Slot{singleDate && totalCells === 1 ? "" : "s"}
                            </button>
                            <button onClick={openExtModal} disabled={submitting || totalCells === 0 || !singleDate}
                                data-testid="mgr-external-book-btn"
                                title={!singleDate ? "External booking requires slots from a single date" : "Booked through another app like Hudle"}
                                className="inline-flex items-center gap-2 px-5 h-11 bg-orange-500 text-slate-950 font-bold uppercase tracking-widest text-xs rounded hover:bg-orange-400 disabled:opacity-40">
                                <ExternalLink className="w-4 h-4" /> Booked via Other App
                            </button>
                            <button onClick={blockNow} disabled={submitting || totalCells === 0}
                                data-testid="mgr-block-btn"
                                className="inline-flex items-center gap-2 px-5 h-11 bg-slate-200 text-slate-900 font-bold uppercase tracking-widest text-xs rounded hover:bg-white disabled:opacity-40">
                                <Lock className="w-4 h-4" /> Block {totalCells} Slot{totalCells === 1 ? "" : "s"}
                            </button>
                        </div>

                        {blocks.length > 0 && (
                            <div className="mt-8">
                                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Existing blocks for {date}</div>
                                <div className="space-y-2">
                                    {blocks.map(b => (
                                        <div key={b.id} className="bg-slate-900 border border-slate-800 rounded p-3 flex items-center justify-between text-sm" data-testid={`mgr-block-${b.id}`}>
                                            <div>
                                                <span className="text-slate-400 font-bold uppercase text-xs tracking-widest">{b.reason}</span>
                                                <span className="text-slate-300 ml-2">{b.slots.join(", ")}</span>
                                            </div>
                                            <button onClick={() => removeBlock(b.id)} className="text-slate-500 hover:text-red-400">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {bookOpen && (
                            <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                                <form onSubmit={submitManualBook} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3" data-testid="mgr-manual-book-form">
                                    <h3 className="font-display text-2xl font-bold uppercase text-white">Manual Booking</h3>
                                    <p className="text-xs text-slate-400">{totalCells} slot(s) · {singleDate} · {facility?.name}</p>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Customer Name</label>
                                        <CustomerNameAutocomplete
                                            value={bookForm.customer_name}
                                            onChange={(v) => setBookForm({ ...bookForm, customer_name: v })}
                                            onSelect={(u) => {
                                                let phone = u.phone || "";
                                                if (phone.startsWith("+91")) phone = phone.slice(3);
                                                setBookForm(f => ({ ...f, customer_name: u.name || "", customer_phone: phone }));
                                            }}
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white"
                                            testid="mgr-book-name-autocomplete"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Phone</label>
                                        <PhoneInput value={bookForm.customer_phone} onChange={(v) => setBookForm({ ...bookForm, customer_phone: v })} testid="mgr-book-phone" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Payment</label>
                                        <select value={bookForm.payment_method} onChange={(e) => setBookForm({ ...bookForm, payment_method: e.target.value })} data-testid="mgr-book-method"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                            <option value="cash">Cash</option>
                                            <option value="upi">UPI</option>
                                            <option value="online">Online (already paid)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Discount (₹)</label>
                                        <input type="number" min="0" value={bookForm.discount} onChange={(e) => setBookForm({ ...bookForm, discount: e.target.value })} data-testid="mgr-book-discount"
                                            placeholder="0"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Paid Amount (₹) — leave empty for full</label>
                                        <input type="number" value={bookForm.paid_amount} onChange={(e) => setBookForm({ ...bookForm, paid_amount: e.target.value })} data-testid="mgr-book-amount"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    {/* Iter 40 — Live math uses SUM of per-slot prices (respects overrides + peak) */}
                                    {(() => {
                                        const slotCount = totalCells || 1;
                                        const perSlot = Math.floor((facility?.hourly_price || 0) / 2);
                                        const priceForSlots = slotPriceSum > 0 ? slotPriceSum : slotCount * perSlot;
                                        const discountVal = Math.max(0, Math.min(parseInt(bookForm.discount) || 0, priceForSlots));
                                        const finalTotal = Math.max(0, priceForSlots - discountVal);
                                        return (
                                            <div className="bg-slate-950 border border-slate-800 rounded p-3 space-y-1" data-testid="mgr-book-live-math">
                                                <div className="flex justify-between text-xs text-slate-400 uppercase tracking-widest">
                                                    <span>Price for {slotCount} slot{slotCount > 1 ? "s" : ""}</span>
                                                    <span className="text-white">₹{priceForSlots.toLocaleString("en-IN")}</span>
                                                </div>
                                                <div className={`flex justify-between text-xs uppercase tracking-widest ${discountVal ? "text-amber-400" : "text-slate-600"}`}>
                                                    <span>Discount</span>
                                                    <span>− ₹{discountVal.toLocaleString("en-IN")}</span>
                                                </div>
                                                <div className="border-t border-slate-800 my-1" />
                                                <div className="flex justify-between text-sm font-bold uppercase tracking-widest text-[#00FF66]" data-testid="mgr-book-final-total">
                                                    <span>Final Total</span>
                                                    <span>₹{finalTotal.toLocaleString("en-IN")}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Notes</label>
                                        <input value={bookForm.notes} onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })} data-testid="mgr-book-notes"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    <label className="flex items-center gap-2 select-none cursor-pointer pt-1">
                                        <input type="checkbox" checked={bookForm.notify_customer}
                                            onChange={(e) => setBookForm({ ...bookForm, notify_customer: e.target.checked })}
                                            data-testid="mgr-book-notify"
                                            className="w-4 h-4 accent-[#00FF66]" />
                                        <span className="text-xs text-slate-300">Send WhatsApp confirmation to customer</span>
                                    </label>
                                    <div className="flex gap-3 pt-2">
                                        <button type="button" onClick={() => setBookOpen(false)} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                        <button type="submit" disabled={submitting} data-testid="mgr-book-submit" className="flex-1 h-11 bg-sky-400 text-slate-950 font-bold uppercase tracking-widest rounded">Confirm Booking</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {extOpen && (
                            <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                                <form onSubmit={submitExternalBook} className="w-full max-w-md bg-slate-900 border border-orange-500/30 rounded-lg p-6 space-y-3" data-testid="mgr-external-book-form">
                                    <h3 className="font-display text-2xl font-bold uppercase text-white inline-flex items-center gap-2">
                                        <ExternalLink className="w-5 h-5 text-orange-400" /> Booked via Other App
                                    </h3>
                                    <p className="text-xs text-slate-400">{totalCells} slot(s) · {singleDate} · {facility?.name}</p>
                                    <p className="text-[11px] text-orange-300/80 bg-orange-500/5 border border-orange-500/20 rounded p-2">
                                        These slots will be blocked. No payment is recorded here — payment was collected on the source app.
                                    </p>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Source App</label>
                                        <input value={extForm.external_source} onChange={(e) => setExtForm({ ...extForm, external_source: e.target.value })} required data-testid="mgr-ext-source-input"
                                            placeholder="Hudle / Playo / Sportobuddy …"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Notes (optional)</label>
                                        <input value={extForm.notes} onChange={(e) => setExtForm({ ...extForm, notes: e.target.value })} data-testid="mgr-ext-notes-input"
                                            placeholder="e.g. order #1234, customer name"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Amount to collect at ground (₹, optional)</label>
                                        <input type="number" min="0" value={extForm.amount_to_collect} onChange={(e) => setExtForm({ ...extForm, amount_to_collect: e.target.value })} data-testid="mgr-ext-amount-to-collect"
                                            placeholder="0"
                                            className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <button type="button" onClick={() => setExtOpen(false)} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                        <button type="submit" disabled={submitting} data-testid="mgr-ext-submit-btn" className="flex-1 h-11 bg-orange-500 text-slate-950 font-bold uppercase tracking-widest rounded hover:bg-orange-400">
                                            {submitting ? "Saving..." : "Confirm Booking"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Unblock (Make Available) modal */}
                        {unblockTarget && (
                            <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="mgr-unblock-modal">
                                <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3">
                                    <h3 className="font-display text-xl font-bold uppercase text-white">Blocked Slot</h3>
                                    <div className="text-sm text-slate-300">
                                        <div>{unblockTarget.date} · {unblockTarget.time}</div>
                                        <div className="text-xs text-slate-400 mt-1">
                                            Reason: <span className="text-amber-400 font-bold uppercase tracking-widest">{unblockTarget.block?.reason || "blocked"}</span>
                                        </div>
                                        {unblockTarget.block?.reason === "school_hours" && (
                                            <div className="mt-2 text-xs text-slate-500 bg-slate-950 border border-slate-800 rounded p-2">
                                                This slot is auto-blocked by the centre's school-hours window. Ask admin to update the centre's schedule.
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <button onClick={() => setUnblockTarget(null)}
                                            className="flex-1 h-11 border border-slate-700 rounded text-slate-300" data-testid="mgr-unblock-cancel">
                                            Cancel
                                        </button>
                                        {unblockTarget.block?.id && (
                                            <button onClick={async () => {
                                                try {
                                                    await api.delete(`/slot-blocks/${unblockTarget.block.id}`);
                                                    toast.success("Slot is now available");
                                                    setUnblockTarget(null);
                                                    setRefreshKey(k => k + 1);
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Failed");
                                                }
                                            }} data-testid="mgr-unblock-confirm"
                                                className="flex-1 h-11 bg-[#00FF66] text-black rounded font-bold uppercase tracking-widest">
                                                Make Available
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ManagerLayout>
    );
}
